import { Card, Stack, Title, useMantineTheme } from "@mantine/core";
import { ContinuousPerformanceStageEvent, PupilSizeData, PupillometryData } from "types/testTypes";

import PupillometryChart from "@components/testContainer/common/PupillometryChart";
import React from "react";
import { getCPTStageReferenceLines } from "@utils/UIUtils";
import { useTranslation } from "react-i18next";

interface PupilSizeChartProps {
  data: PupilSizeData[];
  stageEvents?: ContinuousPerformanceStageEvent[];
}

const PupilSizeChart: React.FC<PupilSizeChartProps> = ({ data, stageEvents }) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const formattedData: PupillometryData[] = data.map((d) => ({
    timestamp: d.timeStamp,
    leftPupil: d.leftPupilSize !== null ? d.leftPupilSize : null,
    rightPupil: d.rightPupilSize !== null ? d.rightPupilSize : null,
  }));

  const verticalReferenceLines = stageEvents ? getCPTStageReferenceLines(stageEvents, true) : [];

  const hasLeftData = formattedData.some((point) => point.leftPupil !== null);
  const hasRightData = formattedData.some((point) => point.rightPupil !== null);
  const hasData = hasLeftData || hasRightData;

  const seriesConfig = [];
  if (hasLeftData) {
    seriesConfig.push({ color: "blue", label: t("leftPupil"), name: "leftPupil" });
  }
  if (hasRightData) {
    seriesConfig.push({ color: "red", label: t("rightPupil"), name: "rightPupil" });
  }

  return (
    <Card withBorder radius="md" shadow="md" p="md">
      <Stack gap="xs">
        <Title order={6} ta="start" mb={theme.spacing.sm}>
          {t("pupilSize")}
        </Title>
        {hasData && (
          <PupillometryChart
            data={formattedData}
            strokeWidth={1}
            referenceLines={verticalReferenceLines}
            height={theme.spacing.chart}
          />
        )}
      </Stack>
    </Card>
  );
};

export default PupilSizeChart;
